import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { BarChart3, Gauge } from 'lucide-react';

export const summarizeAuditorUtilization = (rows) => {
  const withCapacity = (Array.isArray(rows) ? rows : []).filter((row) => Number(row?.totalMandays) > 0);
  return {
    totalAuditors: withCapacity.length,
    nearCapacity: withCapacity.filter((row) => Number(row.confirmedMandays) >= Number(row.totalMandays) * 0.8 && Number(row.confirmedMandays) < Number(row.totalMandays)).length,
    overCapacity: withCapacity.filter((row) => Number(row.confirmedMandays) >= Number(row.totalMandays)).length,
  };
};

export const buildUtilizationChartRows = (rows) => (Array.isArray(rows) ? rows : [])
  .map((row) => {
    const totalMandays = Number(row?.totalMandays) || 0;
    const usedMandays = Number(row?.confirmedMandays) || 0;
    const reportedPercent = Number(row?.utilizationPercent);
    return {
      name: row?.name || row?.fullName || row?.username || 'Unnamed auditor',
      percent: totalMandays > 0 ? Math.round(Number.isFinite(reportedPercent) ? reportedPercent : (usedMandays / totalMandays) * 100) : 0,
      usedMandays,
      totalMandays,
    };
  })
  .sort((left, right) => right.percent - left.percent || right.usedMandays - left.usedMandays)
  .slice(0, 6);

const AXIS_TICK_AMOUNT = 4;

/**
 * A raw "highest percent" max (which can be 950%+ pre-fix) with no fixed tick
 * count made ApexCharts auto-generate one label per ~50%, colliding into an
 * unreadable axis. Fixing the underlying data scope (this-month, confirmed
 * only) keeps percentages sane in practice, but the axis must stay collision-
 * proof regardless — always a small, fixed tick count, and a ceiling rounded
 * to a clean multiple of that tick count so labels land on tidy numbers.
 */
export const buildUtilizationAxisConfig = (chartRows) => {
  const rows = Array.isArray(chartRows) ? chartRows : [];
  const highest = rows.reduce((max, row) => Math.max(max, Number(row?.percent) || 0), 0);
  if (highest <= 100) {
    return { max: 100, tickAmount: AXIS_TICK_AMOUNT };
  }
  const step = Math.ceil((highest / AXIS_TICK_AMOUNT) / 10) * 10;
  return { max: step * AXIS_TICK_AMOUNT, tickAmount: AXIS_TICK_AMOUNT };
};

const utilizationColor = (percent, totalMandays) => {
  if (!totalMandays) return '#94a3b8';
  if (percent >= 100) return '#dc2626';
  if (percent >= 80) return '#d97706';
  return '#1a767e';
};

const AuditorUtilizationSummary = ({ rows, loading, navigate }) => {
  const summary = useMemo(() => summarizeAuditorUtilization(rows), [rows]);
  const chartRows = useMemo(() => buildUtilizationChartRows(rows), [rows]);
  const metrics = [
    ['Active auditors', summary.totalAuditors, '#1a767e'],
    ['Near capacity', summary.nearCapacity, '#d97706'],
    ['Over capacity', summary.overCapacity, '#dc2626'],
  ];
  return (
    <section className="card border-0 shadow-sm mb-4" aria-label="Auditor utilization">
      <div className="card-header bg-transparent d-flex align-items-center gap-2">
        <span className="rounded-2 d-inline-flex align-items-center justify-content-center" style={{ width: 32, height: 32, background: 'rgba(37,99,235,.1)' }}><Gauge size={16} color="#2563eb" /></span>
        <h3 className="card-title fw-semibold mb-0">Auditor Utilization</h3>
        <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => navigate('/tools/auditor-utilization')}>View detail</button>
      </div>
      <div className="card-body">
        <div className="row g-3 text-center">
          {(loading ? metrics.map(([label, , color]) => [label, '-', color]) : metrics).map(([label, value, color]) => (
            <div className="col-4" key={label}><strong className="d-block" style={{ color, fontSize: '1.35rem' }}>{value}</strong><span className="text-muted small">{label}</span></div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-top">
          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            <BarChart3 size={15} color="#2563eb" />
            <span className="small fw-semibold text-uppercase" style={{ color: '#52676d', letterSpacing: '.05em' }}>Highest scheduled capacity</span>
            <span className="badge bg-secondary-lt text-muted ms-auto" style={{ fontSize: '0.68rem', fontWeight: 500 }}>This month · confirmed only</span>
          </div>
          {loading ? <div className="placeholder-glow" style={{ height: 188 }}><span className="placeholder col-12 h-100 rounded" /></div> : chartRows.length === 0 ? <div className="text-muted small py-4 text-center">No auditor capacity data available this month.</div> : (() => {
            const axisConfig = buildUtilizationAxisConfig(chartRows);
            const chartOptions = {
              chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: true, easing: 'easeout', speed: 350 } },
              plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '58%', distributed: true } },
              colors: chartRows.map((row) => utilizationColor(row.percent, row.totalMandays)),
              dataLabels: { enabled: true, textAnchor: 'start', offsetX: 7, formatter: (value, { dataPointIndex }) => `${value}% · ${chartRows[dataPointIndex].usedMandays}/${chartRows[dataPointIndex].totalMandays || '-'}`, style: { colors: ['#314b52'], fontSize: '10px', fontWeight: 700 } },
              xaxis: {
                categories: chartRows.map((row) => row.name),
                min: 0,
                max: axisConfig.max,
                tickAmount: axisConfig.tickAmount,
                labels: { formatter: (value) => `${Math.round(value)}%`, style: { colors: '#809096', fontSize: '10px' } },
              },
              yaxis: { labels: { style: { colors: '#52676d', fontSize: '10px', fontWeight: 600 }, maxWidth: 126 } },
              grid: { borderColor: '#e9eff0', strokeDashArray: 3, padding: { left: 4, right: 34, top: -10, bottom: -4 } },
              legend: { show: false },
              tooltip: { theme: 'light', y: { formatter: (value, { dataPointIndex }) => `${value}% utilization · ${chartRows[dataPointIndex].usedMandays} confirmed of ${chartRows[dataPointIndex].totalMandays || 'unconfigured'} mandays` } },
            };
            return <ReactApexChart options={chartOptions} series={[{ name: 'Utilization', data: chartRows.map((row) => row.percent) }]} type="bar" height={Math.max(190, chartRows.length * 34 + 26)} />;
          })()}
        </div>
      </div>
    </section>
  );
};

export default AuditorUtilizationSummary;
